/**
 * A config wrapped axios instance that checks for standard error responses and shows toastr.errors
 * or a default error if no message is provided on 422 and 400 responses. If no error message is
 * found then the error is sent to Sentry.
 * 
 * We should probably use this in the project-level js bundle at some point but I'm not ready to dive into that
 * without speccing it into a task (we would likely be pummelled with toasts from non standard responses from
 * legacy code and our users don't need that right now)
 */
import axios from 'axios'

// add interceptors to the axios instance
axios.interceptors.response.use(
    response => {
        return response;
    },
    async error => {
        // Ignore request cancellations (e.g. AbortController) so they don't trigger user-facing toasts.
        if (axios.isCancel(error) || error?.code === 'ERR_CANCELED') {
            return Promise.reject(error);
        }

        // If the response is a blob (from file download requests), convert to JSON
        let errorData = error.response?.data;
        if (errorData instanceof Blob) {
            try {
                const errorText = await errorData.text();
                errorData = JSON.parse(errorText);
                error.response.data = errorData;
            } catch (e) {
                // If we can't parse the blob, leave it as is
            }
        }

        // intercept responses that cam from our code
        switch (error.response?.status) {
            // if we have a 422 or 400 response then we want to show the message
            case 422:
            case 400:
                if (errorData?.message) {
                    toastr.warning(
                        errorData.message,
                        "",
                        {
                            timeOut: 8000,
                            preventDuplicates: true
                        }
                    );
                }
                break;
            // ignore 401 responses as they are handled by the auth guard
            case 401:
                break;
            // if we have a 404 response then we want to show the message
            case 404:
                toastr.error("The requested resource could not be found.")
                break;
            // 403 is a forbidden response
            case 403:
                toastr.error("You do not have permission to access this resource.")
                break;
            // we expect 423's when jobs are still processing
            case 423:
                toastr.info("Processing background jobs...", "", {timeOut: 8000, preventDuplicates: true})
                break;
            // we expect 503's when we are updating the system
            case 503:
                toastr.info("The system is currently being updated. Please try again later.", "", {timeOut: 8000, preventDuplicates: true})
                break
            // otherwise we got an unexpected response
            default:
                toastr.error(
                    errorData?.message || "Something went wrong. Please try again.",
                    error.response?.statusText + " (" + error.response?.status + ")",
                    {
                        timeOut: 8000,
                        preventDuplicates: true
                    }
                );
                // @ts-ignore Sentry is included at the page level
                Sentry.captureException(error)
        }
        return Promise.reject(error);
    }
);

export default axios;